<template>
  <div
    class="math-palette"
    v-if="isVisible"
    :class="{ 'is-dragging': isDragging }"
    :style="{ top: position.y + 'px', left: position.x + 'px' }"
    ref="paletteRef"
  >
    <div
      class="palette-header"
      @mousedown="startDrag"
      @touchstart="startDrag"
      style="cursor: move;"
    >
      <h6 class="palette-title">📐 Mathematical Symbols <small style="font-size: 10px; opacity: 0.8;">(drag to move)</small></h6>
      <button class="btn-close-palette" @click="$emit('close')" title="Close">×</button>
    </div>

    <div class="palette-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        :class="['tab-btn', { active: activeTab === tab.id }]"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <div class="palette-content">
      <!-- Opérateurs -->
      <div v-show="activeTab === 'operators'" class="symbol-grid">
        <button v-for="symbol in operators" :key="symbol.latex"
                class="symbol-btn"
                @click="insertSymbol(symbol.latex)"
                :title="symbol.name">
          {{ symbol.display }}
        </button>
      </div>

      <!-- Relations -->
      <div v-show="activeTab === 'relations'" class="symbol-grid">
        <button v-for="symbol in relations" :key="symbol.latex"
                class="symbol-btn"
                @click="insertSymbol(symbol.latex)"
                :title="symbol.name">
          {{ symbol.display }}
        </button>
      </div>

      <!-- Lettres grecques -->
      <div v-show="activeTab === 'greek'" class="symbol-grid">
        <button v-for="symbol in greekLetters" :key="symbol.latex"
                class="symbol-btn greek"
                @click="insertSymbol(symbol.latex)"
                :title="symbol.name">
          {{ symbol.display }}
        </button>
      </div>

      <!-- Symboles spéciaux -->
      <div v-show="activeTab === 'special'" class="symbol-grid">
        <button v-for="symbol in specialSymbols" :key="symbol.latex"
                class="symbol-btn"
                @click="insertSymbol(symbol.latex)"
                :title="symbol.name">
          {{ symbol.display }}
        </button>
      </div>

      <!-- Structures -->
      <div v-show="activeTab === 'structures'" class="structure-grid">
        <button v-for="struct in structures" :key="struct.latex"
                class="structure-btn"
                @click="insertSymbol(struct.latex)"
                :title="struct.name">
          <span class="structure-label">{{ struct.display }}</span>
          <span class="structure-desc">{{ struct.name }}</span>
        </button>
      </div>
    </div>

    <div class="palette-footer">
      <small class="text-muted">Click a symbol to insert it at cursor position</small>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['insert', 'close']);

const activeTab = ref('operators');
const paletteRef = ref(null);
const position = ref({ x: 100, y: 100 });
const isDragging = ref(false);
const dragOffset = ref({ x: 0, y: 0 });

const tabs = [
  { id: 'operators', label: '➕ Operators' },
  { id: 'relations', label: '= Relations' },
  { id: 'greek', label: 'α Greek' },
  { id: 'special', label: '∞ Special' },
  { id: 'structures', label: '📊 Structures' }
];

const operators = [
  { latex: '+', display: '+', name: 'Plus' },
  { latex: '-', display: '−', name: 'Minus' },
  { latex: '\\times', display: '×', name: 'Times' },
  { latex: '\\div', display: '÷', name: 'Division' },
  { latex: '\\pm', display: '±', name: 'Plus-minus' },
  { latex: '\\mp', display: '∓', name: 'Minus-plus' },
  { latex: '\\cdot', display: '·', name: 'Dot' },
  { latex: '\\ast', display: '∗', name: 'Asterisk' },
  { latex: '\\circ', display: '∘', name: 'Circle' },
  { latex: '\\bullet', display: '•', name: 'Bullet' },
];

const relations = [
  { latex: '=', display: '=', name: 'Equals' },
  { latex: '\\neq', display: '≠', name: 'Not equal' },
  { latex: '<', display: '<', name: 'Less than' },
  { latex: '>', display: '>', name: 'Greater than' },
  { latex: '\\leq', display: '≤', name: 'Less or equal' },
  { latex: '\\geq', display: '≥', name: 'Greater or equal' },
  { latex: '\\ll', display: '≪', name: 'Much less' },
  { latex: '\\gg', display: '≫', name: 'Much greater' },
  { latex: '\\approx', display: '≈', name: 'Approximately' },
  { latex: '\\equiv', display: '≡', name: 'Equivalent' },
  { latex: '\\sim', display: '∼', name: 'Similar' },
  { latex: '\\propto', display: '∝', name: 'Proportional' },
];

const greekLetters = [
  { latex: '\\alpha', display: 'α', name: 'Alpha' },
  { latex: '\\beta', display: 'β', name: 'Beta' },
  { latex: '\\gamma', display: 'γ', name: 'Gamma' },
  { latex: '\\delta', display: 'δ', name: 'Delta' },
  { latex: '\\epsilon', display: 'ε', name: 'Epsilon' },
  { latex: '\\zeta', display: 'ζ', name: 'Zeta' },
  { latex: '\\eta', display: 'η', name: 'Eta' },
  { latex: '\\theta', display: 'θ', name: 'Theta' },
  { latex: '\\lambda', display: 'λ', name: 'Lambda' },
  { latex: '\\mu', display: 'μ', name: 'Mu' },
  { latex: '\\pi', display: 'π', name: 'Pi' },
  { latex: '\\rho', display: 'ρ', name: 'Rho' },
  { latex: '\\sigma', display: 'σ', name: 'Sigma' },
  { latex: '\\tau', display: 'τ', name: 'Tau' },
  { latex: '\\phi', display: 'φ', name: 'Phi' },
  { latex: '\\omega', display: 'ω', name: 'Omega' },
  { latex: '\\Gamma', display: 'Γ', name: 'Gamma (maj)' },
  { latex: '\\Delta', display: 'Δ', name: 'Delta (maj)' },
  { latex: '\\Theta', display: 'Θ', name: 'Theta (maj)' },
  { latex: '\\Lambda', display: 'Λ', name: 'Lambda (maj)' },
  { latex: '\\Pi', display: 'Π', name: 'Pi (maj)' },
  { latex: '\\Sigma', display: 'Σ', name: 'Sigma (maj)' },
  { latex: '\\Phi', display: 'Φ', name: 'Phi (maj)' },
  { latex: '\\Omega', display: 'Ω', name: 'Omega (maj)' },
];

const specialSymbols = [
  { latex: '\\infty', display: '∞', name: 'Infinity' },
  { latex: '\\partial', display: '∂', name: 'Partial derivative' },
  { latex: '\\nabla', display: '∇', name: 'Nabla' },
  { latex: '\\sum', display: '∑', name: 'Sum' },
  { latex: '\\prod', display: '∏', name: 'Product' },
  { latex: '\\int', display: '∫', name: 'Integral' },
  { latex: '\\oint', display: '∮', name: 'Contour integral' },
  { latex: '\\lim', display: 'lim', name: 'Limit' },
  { latex: '\\exists', display: '∃', name: 'Exists' },
  { latex: '\\forall', display: '∀', name: 'For all' },
  { latex: '\\in', display: '∈', name: 'Element of' },
  { latex: '\\notin', display: '∉', name: 'Not element of' },
  { latex: '\\subset', display: '⊂', name: 'Subset' },
  { latex: '\\supset', display: '⊃', name: 'Superset' },
  { latex: '\\cup', display: '∪', name: 'Union' },
  { latex: '\\cap', display: '∩', name: 'Intersection' },
  { latex: '\\emptyset', display: '∅', name: 'Empty set' },
  { latex: '\\angle', display: '∠', name: 'Angle' },
  { latex: '\\perp', display: '⊥', name: 'Perpendicular' },
  { latex: '\\parallel', display: '∥', name: 'Parallel' },
];

const structures = [
  { latex: '\\frac{a}{b}', display: 'a/b', name: 'Fraction' },
  { latex: '\\sqrt{x}', display: '√', name: 'Square root' },
  { latex: '\\sqrt[n]{x}', display: 'ⁿ√', name: 'Nth root' },
  { latex: 'x^{n}', display: 'xⁿ', name: 'Superscript' },
  { latex: 'x_{n}', display: 'xₙ', name: 'Subscript' },
  { latex: '\\sum_{i=1}^{n}', display: '∑', name: 'Sum with limits' },
  { latex: '\\int_{a}^{b}', display: '∫', name: 'Integral with limits' },
  { latex: '\\lim_{x\\to a}', display: 'lim', name: 'Limit' },
  { latex: '\\begin{pmatrix}a&b\\\\c&d\\end{pmatrix}', display: '( )', name: 'Matrix (parentheses)' },
  { latex: '\\begin{bmatrix}a&b\\\\c&d\\end{bmatrix}', display: '[ ]', name: 'Matrix (brackets)' },
  { latex: '\\vec{v}', display: 'v⃗', name: 'Vector' },
  { latex: '\\overline{x}', display: 'x̄', name: 'Overline' },
];

// Fonctions de drag & drop
const startDrag = (e) => {
  e.preventDefault();
  isDragging.value = true;

  const clientX = e.clientX || e.touches?.[0]?.clientX;
  const clientY = e.clientY || e.touches?.[0]?.clientY;

  dragOffset.value = {
    x: clientX - position.value.x,
    y: clientY - position.value.y
  };

  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
  document.addEventListener('touchmove', onDrag);
  document.addEventListener('touchend', stopDrag);
};

const onDrag = (e) => {
  if (!isDragging.value) return;

  const clientX = e.clientX || e.touches?.[0]?.clientX;
  const clientY = e.clientY || e.touches?.[0]?.clientY;

  // Calculer la nouvelle position
  let newX = clientX - dragOffset.value.x;
  let newY = clientY - dragOffset.value.y;

  // Contraindre la palette dans la fenêtre
  const paletteWidth = paletteRef.value?.offsetWidth || 450;
  const paletteHeight = paletteRef.value?.offsetHeight || 400;

  newX = Math.max(0, Math.min(newX, window.innerWidth - paletteWidth));
  newY = Math.max(0, Math.min(newY, window.innerHeight - paletteHeight));

  position.value = { x: newX, y: newY };
};

const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
  document.removeEventListener('touchmove', onDrag);
  document.removeEventListener('touchend', stopDrag);
};

const insertSymbol = (latex) => {
  emit('insert', latex);
};

onBeforeUnmount(() => {
  stopDrag();
});
</script>

<style scoped>
.math-palette {
  position: fixed;
  background: white;
  border: 2px solid #3498db;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 450px;
  max-width: 90vw;
  z-index: 1000;
  animation: slideDown 0.2s ease-out;
  user-select: none;
  transition: box-shadow 0.2s ease;
}

.math-palette.is-dragging {
  box-shadow: 0 8px 30px rgba(52, 152, 219, 0.4);
  cursor: move;
}

.math-palette.is-dragging * {
  cursor: move !important;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.palette-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  border-radius: 6px 6px 0 0;
}

.palette-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.btn-close-palette {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.btn-close-palette:hover {
  background: rgba(255, 255, 255, 0.2);
}

.palette-tabs {
  display: flex;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  overflow-x: auto;
}

.tab-btn {
  flex: 1;
  padding: 8px 12px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 12px;
  color: #6c757d;
  transition: all 0.2s;
  white-space: nowrap;
  border-bottom: 2px solid transparent;
}

.tab-btn:hover {
  background: #e9ecef;
  color: #495057;
}

.tab-btn.active {
  color: #3498db;
  border-bottom-color: #3498db;
  background: white;
  font-weight: 600;
}

.palette-content {
  padding: 16px;
  max-height: 300px;
  overflow-y: auto;
}

.symbol-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(45px, 1fr));
  gap: 6px;
}

.symbol-btn {
  padding: 10px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 20px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 45px;
}

.symbol-btn:hover {
  background: #e3f2fd;
  border-color: #3498db;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.2);
}

.symbol-btn:active {
  transform: translateY(0);
}

.symbol-btn.greek {
  font-family: 'Times New Roman', serif;
  font-size: 22px;
}

.structure-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.structure-btn {
  padding: 12px 16px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
}

.structure-btn:hover {
  background: #e3f2fd;
  border-color: #3498db;
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.2);
}

.structure-label {
  font-size: 20px;
  font-weight: bold;
  color: #3498db;
  min-width: 40px;
  text-align: center;
}

.structure-desc {
  font-size: 13px;
  color: #6c757d;
}

.palette-footer {
  padding: 8px 16px;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
  border-radius: 0 0 6px 6px;
  text-align: center;
}

.palette-footer small {
  font-size: 11px;
  color: #6c757d;
}

/* Scrollbar styling */
.palette-content::-webkit-scrollbar {
  width: 6px;
}

.palette-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.palette-content::-webkit-scrollbar-thumb {
  background: #3498db;
  border-radius: 3px;
}

.palette-content::-webkit-scrollbar-thumb:hover {
  background: #2980b9;
}
</style>
